/*
Author: Chathuraka Mallawa Arachchi
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import { SearchIcon } from '@/assets'
import { RootState } from '@/reducers/store'
import { useAddNewField, useValidate } from '@/state/survey/formSlice'
import { debounce } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Select, { components } from 'react-select'
import { ValidationManager } from '../survey/SurveyModel'
import {
    DropDownOption,
    nodeConfigurations,
} from '../survey/SurveyModel/types/nodeTypes'
import Errors from './Error'

const MultiSelectPopUp = (props: any) => {
    const {
        options,
        name,
        id,
        placeholder = 'Search',
        handleChange,
        value,
        excludeOptions,
        propDivClasses = '',
        isInValid = true,
        onBlur,
        disabled,
        configurations,
        handleCustomSearchInputTextChange,
        autoFocus,
        menuPositionOffset,
    } = props
    const [optionList, setOptionList] = useState<DropDownOption[]>(options)
    const [isFocused, setIsFocused] = useState(false)
    const form = useSelector((state: RootState) => state.form)
    const addNewFieldValue = useAddNewField()
    const validateInputField = useValidate()
    const [isVisible, setIsVisible] = useState(true)
    const selectRef = useRef<any>(null)
    const [searchInputValue, setSearchInputValue] = useState(
        props.inputValue || ''
    )
    const normalizedValue = Array.isArray(value) ? value : []
    const popupWrapperStyle = {
        ...(menuPositionOffset?.top || menuPositionOffset?.left || menuPositionOffset?.transform
            ? {
                  position: 'relative' as const,
                  top: menuPositionOffset?.top ?? '0px',
                  left: menuPositionOffset?.left ?? '0px',
                  transform: menuPositionOffset?.transform ?? 'none',
              }
            : {}),
        ...(menuPositionOffset?.width
            ? { width: menuPositionOffset.width }
            : {}),
    }

    useEffect(() => {
        const fieldNode = {
            name: id,
            type: 'SingleLineText',
        }
        if (!form[fieldNode.name]) {
            const validatedForm =
                ValidationManager.applyCommonValidationsWithValue(fieldNode, '')
            addNewFieldValue(fieldNode.name, validatedForm)
        }
    }, [])

    useEffect(() => {
        if (!autoFocus) return
        if (!selectRef.current) return
        // Defer to ensure react-select input exists
        setTimeout(() => selectRef.current?.focus?.(), 0)
    }, [autoFocus])

    useEffect(() => {
        if (props.inputValue === undefined || isFocused) {
            return
        }
        const nextValue = props.inputValue || ''
        setSearchInputValue(nextValue)
    }, [props.inputValue, isFocused])

    const handleOnFocus = () => {
        setIsFocused(true)
    }

    const CustomSearchIndicator = (props: any) => {
        const handleSearchClick = (e: React.MouseEvent) => {
            e.stopPropagation()
        }

        return (
            <components.DropdownIndicator {...props}>
                <button
                    onMouseDown={handleSearchClick}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <SearchIcon width={20} height={20} />
                </button>
            </components.DropdownIndicator>
        )
    }

    const prepareTypeOptions = () => {
        let newoptionList: DropDownOption[] = []
        if (excludeOptions.length) {
            newoptionList = options.filter((rowItem: DropDownOption) => {
                return !excludeOptions.some(
                    (item: DropDownOption) =>
                        item.value === rowItem.value && item.value !== value
                )
            })
        }

        setOptionList(newoptionList)
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!(event.target as HTMLElement).closest('.select-wrapper')) {
                setIsVisible(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        if (excludeOptions && excludeOptions?.length && options.length) {
            prepareTypeOptions()
        }
    }, [excludeOptions, options])

    useEffect(() => {
        if (!excludeOptions || !excludeOptions.length) {
            setOptionList(options)
        }
    }, [excludeOptions, options])

    const handleOnBlure = () => {
        onBlur && onBlur()
        setIsFocused(false)
    }

    const formatOptionLabel = (
        { label, description }: DropDownOption,
        { context }: any
    ) => {
        return context === 'menu' ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div>{label}</div>
                <div>{description}</div>
            </div>
        ) : (
            <div>{label}</div>
        )
    }

    const CheckboxOption = (props: any) => {
        return (
            <components.Option {...props}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft:
                            'var(--comp-listscreenfiltermenu-padding-xs)',
                        paddingRight:
                            'var(--comp-listscreenfiltermenu-padding-xs)',
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                        <rect
                            x="0"
                            y="0"
                            width="18"
                            height="18"
                            rx="0"
                            ry="0"
                            fill={props.isSelected ? 'white' : 'white'}
                            stroke="black"
                            strokeWidth="1"
                        />
                        {props.isSelected && (
                            <polyline
                                points="4.5,9 7.5,12.5 13.5,5.5"
                                fill="none"
                                stroke="#3F3F3F"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        )}
                    </svg>
                    <label
                        style={{
                            marginLeft: '8px',
                            fontSize: '14px',
                            fontWeight: '400',
                        }}
                    >
                        {props.label}
                    </label>
                </div>
            </components.Option>
        )
    }

    const CustomMenu = (props: any) => (
        <components.Menu {...props}>{props.children}</components.Menu>
    )

    const CustomPlaceholder = (props: any) => {
        if (props.isFocused) {
            return null
        }
        return <components.Placeholder {...props} />
    }

    const debouncedValidate = useMemo(() =>
        debounce((id: string, val: string) => {
            validateInputField(id, val, true)
        }, 300),
        []
    )
    const CustomClearIndicator = () => null

    const clearSearchText = () => {
        if (searchInputValue === '' && (props.inputValue ?? '') === '') return
        setSearchInputValue('')
        props.onInputValueChange?.('')
        handleCustomSearchInputTextChange?.('')
        debouncedValidate(id, '')
    }

    useEffect(() => {
        if (!isVisible) {
            clearSearchText()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible])

    useEffect(() => {
        return () => {
            debouncedValidate.cancel()
        }
    }, [debouncedValidate])

    return (
        <div>
            {isVisible ? (
                <div
                    className={`select-wrapper ${id}  ${isInValid ? 'error' : ''}`}
                    style={popupWrapperStyle}
                >
                    <div
                        className={`${propDivClasses} popup-checkbox-slect input-div ${configurations?.some((config: nodeConfigurations) => config.key === 'FieldWidthPct' && config.value === '0.5') ? 'width-half' : ''} ${isFocused ? 'input-div-focus-controller' : ''}`}
                    >
                        <Select
                            ref={selectRef}
                            className={`custom-select${configurations?.some((config: nodeConfigurations) => config.key === 'FieldWidthPct' && config.value === '0.5') ? 'custom-select-lg' : ''}${disabled ? 'disabled' : ''}`}
                            name={name}
                            placeholder={placeholder}
                            options={optionList}
                            onBlur={handleOnBlure}
                            onChange={handleChange}
                            menuIsOpen={true}
                            closeMenuOnSelect={false}
                            controlShouldRenderValue={false}
                            value={normalizedValue}
                            inputValue={searchInputValue}
                            classNamePrefix="custom-select"
                            onInputChange={(value, { action }) => {
                                if (action === 'input-change') {
                                    setSearchInputValue(value)
                                    props.onInputValueChange?.(value)
                                    debouncedValidate(id, value)
                                    handleCustomSearchInputTextChange?.(value)
                                }
                                return value
                            }}
                            hideSelectedOptions={false}
                            onFocus={handleOnFocus}
                            isMulti
                            isSearchable={true}
                            components={{
                                Option: CheckboxOption,
                                DropdownIndicator: CustomSearchIndicator,
                                Menu: CustomMenu,
                                Placeholder: CustomPlaceholder,
                                ClearIndicator: CustomClearIndicator,
                                IndicatorSeparator: null, 
                            }}
                            formatOptionLabel={formatOptionLabel}
                            noOptionsMessage={() => null}
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    marginTop:
                                        'var(--comp-listscreenfiltermenu-padding-xs)',
                                    marginLeft:
                                        'var(--comp-listscreenfiltermenu-padding-xs)',
                                    marginRight:
                                        'var(--comp-listscreenfiltermenu-padding-xs)',
                                    marginBottom:
                                        'var(--comp-listscreenfiltermenu-padding-xs)',
                                    ':hover': {
                                        borderColor: state.isFocused
                                            ? `var(--comp-field-bordercolor-focus)`
                                            : `var(--comp-field-bordercolor-default)`,
                                    },
                                    boxShadow: state.isFocused
                                        ? '0 0 0 2px var(--comp-field-bordercolor-focus)'
                                        : 'none',
                                    transition: 'none',
                                    borderRadius: '0',
                                    borderColor: state.isFocused
                                        ? 'var(--comp-field-bordercolor-focus)'
                                        : 'var(--comp-field-bordercolor-default)',
                                    fontSize:
                                        'var(--comp-field-fontsize-input-default)',
                                    fontWeight:
                                        'var(--comp-field-fontweight-input-default)',
                                    color: 'var(--comp-field-fontcolour-default)',
                                    fontFamily: '$Lexend',
                                    boxSizing: 'border-box',
                                    height: 'var(--input-height)',
                                    minHeight: 'var(--input-height)',
                                }),
                                dropdownIndicator: (
                                    provided: any,
                                    state: any
                                ) => ({
                                    ...provided,
                                    paddingLeft: 11,
                                    color: state.isFocused
                                        ? 'var(--comp-field-fontcolour-default)'
                                        : 'var(--comp-icon-color-positive)', // Change arrow color
                                    ':hover': {
                                        color: 'darkblue', // Change color on hover
                                    },
                                    display: disabled ? 'none' : 'flex',
                                }),
                                menu: (provided: any) => ({
                                    ...provided,
                                    position: 'static',
                                    borderRadius: 0,
                                    marginTop: 0,
                                    padding: '0px 0px',
                                    width: 'calc(100%)',
                                    left: '-2px',
                                    boxShadow: 'none',
                                }),
                                menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: 'none',
                                    height: 'auto',
                                    overflow: 'visible'
                                }),
                                valueContainer: (baseStyles) => ({
                                    ...baseStyles,
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%',
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                    paddingLeft: 8,
                                    paddingRight: 8,
                                    flexWrap: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }),
                                input: (baseStyles) => ({
                                    ...baseStyles,
                                    margin: 0,
                                    padding: 0,
                                    marginLeft: 0,
                                    lineHeight:
                                        'var(--comp-field-lineheight-input-default)',
                                }),
                                singleValue: (baseStyles) => ({
                                    ...baseStyles,
                                    fontSize:
                                        'var(--comp-field-fontsize-input-default)',
                                    fontWeight:
                                        'var(--comp-field-fontweight-input-default',
                                    color: 'var(--comp-field-fontcolour-default)',
                                }),
                                multiValue: (base) => ({
                                    ...base,
                                    whiteSpace: 'nowrap',
                                }),
                                indicatorsContainer: (base) => ({
                                    ...base,
                                    flexShrink: 0,
                                }),
                                placeholder: (baseStyles) => ({
                                    ...baseStyles,
                                    marginLeft: 0,
                                    marginRight: 0,
                                    fontSize:
                                        'var(--comp-field-fontsize-input-default)',
                                    fontWeight:
                                        'var(--comp-field-fontweight-input-default',
                                    color: 'var(--comp-field-fontcolour-input-default)',
                                }),
                                option: (baseStyles, state) => {
                                    const { padding: _p, ...optionBase } =
                                        baseStyles as typeof baseStyles & {
                                            padding?: unknown
                                        }
                                    return {
                                        ...optionBase,
                                        paddingTop: 8,
                                        paddingRight: 0,
                                        paddingBottom: 8,
                                        paddingLeft: 0,
                                        color: state.isSelected
                                        ? state.isFocused
                                            ? '#fff'
                                            : 'var(--comp-field-fontcolour-input-default)'
                                        : state.isFocused
                                          ? '#fff'
                                          : 'var(--comp-field-fontcolour-input-default)', // Font color for options
                                    backgroundColor: state.isFocused
                                        ? 'var(--comp-field-bgcolor-menuoptionactive)'
                                        : 'var(--comp-field-fontcolor-menu-active)', // Background for focused options
                                    fontSize: '14px', // Font size for dropdown items
                                    }
                                },
                            }}
                        ></Select>
                    </div>
                    {form ? (
                        <Errors
                            fieldName={name}
                            validation={form[name] ? form[name] : undefined}
                        />
                    ) : (
                        ''
                    )}
                </div>
            ) : null}
        </div>
    )
}
export default React.memo(MultiSelectPopUp)
